import { getLocalizedDefaults, type SupportedLanguage } from '@shared/core/localized-defaults'
import { UserPreferencesResponse } from '@shared/core/preferences'
import { MessageType } from '@shared/core/reminders'
import { MessageBuilder, SAMPLE_CONFIRMATION_LINK } from '@shared/core/sms/message-builder'
import type { MessageTemplateVariables } from '@shared/core/sms/message-builder.interface'
import { templateTokens } from '@shared/core/sms/template-token'
import { resolvePreviewVideoLink } from '@shared/core/sms/video-link-token'
import { format } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { SegmentedMessage } from 'sms-segments-calculator'
import { getEventDate } from 'src/helpers/get-event-date'
import { formatDateInLocale } from 'src/helpers/locales'
import { DateOrders } from 'src/modules/user/types/date-orders.types'
import { resolveProfileMessageLanguage } from 'src/modules/user/utils/resolve-profile-message-language'
import { stripHtmlTags } from './helpers'

/** English example suffix for previews; localized previews use the catalog via {@link buildExampleConfirmationSuffix}. */
export const EXAMPLE_CONFIRMATION_MESSAGE: string = buildExampleConfirmationSuffix('en')

export interface MessageTemplateData {
  sender_name?: string | null
  location?: string | null
  confirmation_message?: string | null
  reminder_message?: string | null
  follow_up_message?: string | null
  language?: SupportedLanguage | null
}

export interface EventData extends MessageTemplateData {
  title?: string | null
  all_day?: boolean | null
  event_date: string
  event_location?: string | null
  video_link?: string | null
  timezone?: string | null
}

export interface CalendarData {
  timezone: string
  timezone_custom?: string | null
}

export interface UserData {
  use_event_timezone?: boolean | null
  google_calendar_date_order?: keyof DateOrders | null
  google_calendar_24_hour_time?: boolean | null
  appointment_confirmation?: boolean | null
  default_country_code?: string | null
  message_language?: SupportedLanguage | null
  default_message_template?: MessageTemplateData
}

export interface ConfirmationLinkData {
  id: string
  linkUrl: string
}

export interface GenerateEventMessageResult {
  message: string
  messageLength: number
  segmentsCount: number
}

export interface TimezonePlaceholderMismatchInput {
  profile: UserData
  preferences: UserPreferencesResponse
  event: EventData
  calendar: CalendarData
  displayTimezone: string
  confirmationEnabled: boolean
  reminderEnabled: boolean
  followUpEnabled: boolean
}

export function buildExampleConfirmationSuffix(language: SupportedLanguage): string {
  const suffixTemplate = getLocalizedDefaults(language).confirmationLinkSuffix
  return ` ${suffixTemplate.replace('{url}', SAMPLE_CONFIRMATION_LINK.linkUrl)}`
}

export function resolvePreviewLanguage(
  profile: UserData,
  templateLanguage?: SupportedLanguage | null,
): SupportedLanguage {
  return resolveProfileMessageLanguage(profile, templateLanguage).language
}

export function shouldWarnTimezoneDateTimePlaceholderMismatch(input: TimezonePlaceholderMismatchInput): boolean {
  const {
    profile,
    preferences,
    event,
    calendar,
    displayTimezone,
    confirmationEnabled,
    reminderEnabled,
    followUpEnabled,
  } = input

  const smsTimezone = getEffectiveMessageTimezone(event, calendar, profile)
  const smsParts = formatEventDateTimeForMessage(event, preferences, smsTimezone)
  const displayParts = formatEventDateTimeForMessage(event, preferences, displayTimezone)

  const types: { type: MessageType; enabled: boolean }[] = [
    { type: 'confirmation', enabled: confirmationEnabled },
    { type: 'reminder', enabled: reminderEnabled },
    { type: 'follow_up', enabled: followUpEnabled },
  ]

  for (const { type, enabled } of types) {
    if (!enabled) continue

    const template = getResolvedMessageTemplate(type, event, profile)
    const { usesDate, usesTime } = getTemplateDateTimePlaceholderUsage(template)

    if (usesDate && smsParts.date !== displayParts.date) {
      return true
    }
    if (usesTime && smsParts.time !== displayParts.time) {
      return true
    }
  }

  return false
}

export function generateEventMessage(
  type: MessageType,
  profile: UserData,
  preferences: UserPreferencesResponse,
  event: EventData,
  calendar: CalendarData,
  templateLanguage?: SupportedLanguage | null,
): GenerateEventMessageResult {
  if (!profile || !event) {
    return {
      message: '',
      messageLength: 0,
      segmentsCount: 0,
    }
  }

  const variables = extractVariables(event, calendar, profile, preferences)
  const language = resolvePreviewLanguage(profile, templateLanguage)

  const unprocessedMessage = pickMessageTemplate(type, event, profile?.default_message_template)

  const rawProcessedMessage = processMessage(
    type,
    unprocessedMessage,
    variables,
    profile.appointment_confirmation,
    false,
    language,
  )

  const colorizedProcessedMessage = processMessage(
    type,
    unprocessedMessage,
    variables,
    profile.appointment_confirmation,
    true,
    language,
  )

  return {
    message: colorizedProcessedMessage,
    messageLength: rawProcessedMessage.length,
    segmentsCount: new SegmentedMessage(rawProcessedMessage).segmentsCount,
  }
}

export function generateEstimatedEventMessage(
  type: MessageType,
  profile: UserData,
  preferences: UserPreferencesResponse,
  templateLanguage?: SupportedLanguage | null,
): GenerateEventMessageResult {
  if (!profile) {
    return {
      message: '',
      messageLength: 0,
      segmentsCount: 0,
    }
  }

  return generateEventMessage(
    type,
    {
      ...profile,
      default_message_template: {
        ...profile?.default_message_template,
        sender_name: profile?.default_message_template.sender_name || 'Name or Business',
        location: profile?.default_message_template.location || '78 Test Ave, Suite 5D',
      },
    },
    preferences,
    {
      title: 'Sample Appointment Title',
      event_location: '456 Awesome Street, Suite 34D, Sample Town',
      video_link: 'https://meet.google.com/abc-defg-hij',
      event_date: new Date().getTime().toString(),
      timezone: 'America/New_York',
    },
    {
      timezone: 'America/New_York',
      timezone_custom: null,
    },
    templateLanguage,
  )
}

function extractVariables(
  event: EventData,
  calendar: CalendarData,
  profile: UserData,
  preferences: UserPreferencesResponse,
): MessageTemplateVariables {
  const timezoneForDisplay = getEffectiveMessageTimezone(event, calendar, profile)
  const { date, time } = formatEventDateTimeForMessage(event, preferences, timezoneForDisplay)

  return {
    title: event?.title || '',
    eventlocation: event?.event_location || '',
    videolink: resolvePreviewVideoLink(event?.video_link),
    location: event?.location ?? profile?.default_message_template?.location ?? '',
    from: event?.sender_name ?? profile?.default_message_template?.sender_name ?? '',
    date,
    time,
  }
}

function pickMessageTemplate(
  type: MessageType,
  ...data: Array<{
    confirmation_message?: string | null
    reminder_message?: string | null
    follow_up_message?: string | null
  }>
): string {
  if (!data) return ''
  if (!Array.isArray(data)) data = [data]

  for (const entry of data) {
    switch (type) {
      case 'confirmation':
        if (entry?.confirmation_message) return entry.confirmation_message
        break
      case 'reminder':
        if (entry?.reminder_message) return entry.reminder_message
        break
      case 'follow_up':
        if (entry?.follow_up_message) return entry.follow_up_message
        break
    }
  }

  return ''
}

function processMessage(
  type: MessageType,
  message: string,
  variables: MessageTemplateVariables,
  appointmentConfirmation: boolean,
  colorize: boolean,
  language: SupportedLanguage,
): string {
  const template = stripHtmlTags(message)
  const confirmationLink = appointmentConfirmation && type === 'reminder' ? SAMPLE_CONFIRMATION_LINK : null

  const built = MessageBuilder.build(template, variables, confirmationLink, language)

  if (!colorize) {
    return built.trim()
  }

  return applyColorizedHighlights(built, variables).trim()
}

function applyColorizedHighlights(message: string, variables: MessageTemplateVariables): string {
  const open = '<span style="color: blue;">'
  const close = '</span>'

  const values = [
    variables.title,
    variables.eventlocation,
    variables.videolink,
    variables.location,
    variables.from,
    variables.date,
    variables.time,
  ]
    .filter((value): value is string => Boolean(value))
    .sort((a, b) => b.length - a.length)

  let highlighted = message
  for (const value of values) {
    highlighted = highlighted.split(value).join(`${open}${value}${close}`)
  }

  return highlighted
}

export function getEffectiveMessageTimezone(
  event: EventData,
  calendar: CalendarData,
  profile: UserData,
): string | null {
  return (
    (profile.use_event_timezone && event.timezone !== calendar.timezone ? event.timezone : null) ||
    calendar.timezone_custom ||
    calendar.timezone
  )
}

export function formatEventDateTimeForMessage(
  event: EventData,
  preferences: UserPreferencesResponse,
  timezoneForDateTime: string | null | undefined,
): { date: string; time: string } {
  const eventDate = getEventDate(event)
  const hourFormat = preferences?.user_preferences.hour_format === 'h24' ? 'HH:mm' : 'h:mma'

  return {
    date: formatDateInLocale(
      eventDate,
      preferences?.user_preferences.locale,
      preferences?.user_preferences.date_field_order,
      timezoneForDateTime,
    ),
    time: event.all_day
      ? 'All Day'
      : timezoneForDateTime
        ? formatInTimeZone(eventDate, timezoneForDateTime, hourFormat)
        : format(eventDate, hourFormat),
  }
}

function getResolvedMessageTemplate(type: MessageType, event: EventData, profile: UserData): string {
  return pickMessageTemplate(type, event, profile?.default_message_template)
}

function getTemplateDateTimePlaceholderUsage(template: string): {
  usesDate: boolean
  usesTime: boolean
} {
  const tokens = templateTokens(stripHtmlTags(template))
  return { usesDate: tokens.has('date'), usesTime: tokens.has('time') }
}
